import React, { useState, useEffect, useMemo, useRef } from "react";
import { useRouter, usePathname } from "next/navigation";
import { createUseStyles } from "react-jss";
import { Company } from "@/common/userfuctions";
import {
  SettingOutlined,
  HomeFilled,
} from "@ant-design/icons";
import SafeFontAwesomeIcon from "./SafeFontAwesomeIcon";
import { faBriefcase, faUsers, faChartSimple } from "@fortawesome/free-solid-svg-icons";
import { getEmployerSettingsTabs } from "@/pages/api/Employer/Employer";
import { useSelector } from "react-redux";
import { DynamicSkeleton } from "./skeleton";
const Sidebar = () => {
  const styles = useStyles();
  const router = useRouter();
  const pathname = usePathname();
  const [selectedMenu, setSelectedMenu] = useState("");
  const [settingsRoute, setSettingsRoute] = useState("/employer/settings");
  const CompanyCode = Company.getCompany();
  const employerRole = useSelector((state) => state.employer.roles);
  const [roleLoaded, setRoleLoaded] = useState(() => employerRole != null);
  const settingsRouteCacheRef = useRef(null);
  useEffect(() => {
    if (employerRole !== null && employerRole !== undefined) {
      setRoleLoaded(true);
    }
  }, [employerRole]);
  const loading = !roleLoaded;

  const goToSettings = async () => {
    try {
      if (!settingsRouteCacheRef.current) {
        const res = await getEmployerSettingsTabs();
        const tabs = Array.isArray(res?.Data?.Tabs) ? res.Data.Tabs : [];
        settingsRouteCacheRef.current = tabs[0]?.link || "/employer/settings";
      }
      setSettingsRoute(settingsRouteCacheRef.current);
      router.push(settingsRouteCacheRef.current);
    } catch (e) {
      setSettingsRoute("/employer/settings");
      router.push("/employer/settings");
    }
  };

  const menuItems = useMemo(
    () =>
      [
        employerRole === 4
          ? null
          : { key: "home", icon: <HomeFilled />, label: "Home", route: "/employer/home" },
        { key: "jobs", icon: <SafeFontAwesomeIcon icon={faBriefcase} />, label: "Jobs", route:employerRole === 4 ? "/employer/jobs?pg=1&tabIndex=3" : "/employer/jobs?pg=1" },
        employerRole === 4
          ? null
          : { key: "candidates", icon: <SafeFontAwesomeIcon icon={faUsers} />, label: "Candidates", route: "/employer/candidates" },
        employerRole === 4
          ? null
          : employerRole === 3
            ? null
            : { key: "analytics", icon: <SafeFontAwesomeIcon icon={faChartSimple} />, label: "Analytics", route: "/employer/analytics" },
      ].filter(Boolean),
    [employerRole]
  );

  const footerItems = useMemo(
    () =>
      [
        employerRole === 4
          ? null
          : employerRole === 3
            ? null
            : { key: "settings", icon: <SettingOutlined />, label: "Settings", route: null },
      ].filter(Boolean),
    [employerRole]
  );

  useEffect(() => {
    const pathSegment = (pathname?.split("/")[2] ?? pathname?.split("/")[1]) || "";
    let activeItem;

    if (pathSegment.includes("my-companies") || pathSegment.includes("settings")) {
      activeItem = footerItems.find((item) => item.key === pathSegment);
    } else if (pathSegment === "postajob") {
      activeItem = menuItems.find((item) => item.key === "jobs");
    } else {
      activeItem = menuItems.find((item) => item.key === pathSegment);
    }

    if (activeItem) setSelectedMenu(activeItem.key);
  }, [pathname, menuItems, footerItems]);

  const handleMenuClick = (item) => {
    setSelectedMenu(item.key);
    if (item.key === "settings") return goToSettings();
    router.push(item.route);
  };

  return (
    <div className={styles.sidebarContainer}>
      {loading ? (
        <div className={styles.menuLoader}>
          <DynamicSkeleton type="squareBox" width={50} height={50} />
        </div>
      ) : (
        <div className={styles.menu}>
          {menuItems.map((item) => (
            <div
              key={item.key}
              className={`${styles.menuItem} ${selectedMenu === item.key ? styles.selected : ""}`}
              onClick={() => handleMenuClick(item)}
            >
              {item.icon}
              <span className={styles.menuLabel}>{item.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Footer Menu */}
      <div className={styles.footer}>
        {footerItems.map(
          (item) =>
            CompanyCode && (
              <div
                key={item.key}
                className={`${styles.menuItem} ${selectedMenu === item.key ? styles.selected : ""}`}
                onClick={() => handleMenuClick(item)}
              >
                {item.icon}
                <span className={styles.menuLabel}>{item.label}</span>
              </div>
            )
        )}
      </div>
    </div>
  );
};

export default Sidebar;


const useStyles = createUseStyles({
  sidebarContainer: {
    width: "100px",
    height: "calc(100vh - 100px)",
    maxHeight: "calc(100vh - 100px)",
    overflowY: "auto",
    overflowX: "hidden",
    position: "sticky",
    top: 100,
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fff",
    transition: "width 0.3s ease",

    scrollbarWidth: "none",
    msOverflowStyle: "none",

    "&::-webkit-scrollbar": {
      display: "none",
    },

    "@media (max-height: 700px)": {
      top: 80,
      height: "calc(100vh - 80px)",
      maxHeight: "calc(100vh - 80px)",
    },

    "@media (max-width: 768px)": {
      width: "90px",
      top: 80,
      height: "calc(100vh - 80px)",
      maxHeight: "calc(100vh - 80px)",
    },

    "@media (max-width: 425px)": {
      width: "70px",
    },
  },
  logo: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    margin: "20px 0px",
  },
  logoImage: {
    height: "40px",
    transition: "transform 0.3s ease",
  },
  menuLoader: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
  },
  menu: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
  },
  footer: {
    padding: "10px 0",
  },
  menuItem: {
    width: "100%",
    padding: "12px 20px",
    display: "flex",
    gap: "5px",
    flexDirection: "column",
    alignItems: "center",
    color: "gray",
    cursor: "pointer",
    transition: "background-color 0.3s ease, padding 0.3s ease",
    position: "relative",

    "&:hover": {
      backgroundColor: "#f5f5f5",
    },

    "& svg": {
      color: "gray",
      fontSize: "24px",
    },

    "@media (max-height: 700px)": {
      padding: "8px 16px",

      "& svg": {
        fontSize: "20px",
      },
    },
  },
  selected: {
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: "6px",
      height: "40px",
      borderTopRightRadius: "10px",
      borderBottomRightRadius: "10px",
      backgroundColor: "#2364ae",
      "@media (max-width: 320px)": {
        width: '4px'
      }
    },
    "& span": {
      fontWeight: "700",
      color: '#2364ae',
      fontSize: "12px",
      "@media (max-width: 320px)": {
        fontSize: "8px"
      }
    },
    "& svg": {
      color: '#2364ae',
    },
  },
  menuLabel: {
    fontSize: "12px",
    textAlign: "center",
    color: "#000",
    "@media (max-width: 320px)": {
      fontSize: "10px"
    }
  },
});
